import { forwardRef } from 'react';

interface FormInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>, 'onChange'> {
  label?: string;
  error?: string;
  hint?: string;
  /** Render as textarea */
  multiline?: boolean;
  rows?: number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

const inputClasses =
  'w-full px-3 py-2.5 rounded-xl border border-[var(--c-border)] bg-[var(--c-card)] focus-input disabled:bg-[var(--c-surface)] disabled:opacity-70 text-[var(--c-text)] text-sm font-medium placeholder:text-[var(--c-muted)] transition-colors';

export const FormInput = forwardRef<HTMLInputElement | HTMLTextAreaElement, FormInputProps>(
  ({ label, error, hint, multiline, rows = 3, className = '', ...props }, ref) => {
    return (
      <div className="space-y-1">
        {label && (
          <label className="text-[11px] uppercase tracking-[0.08em] text-[var(--c-muted)] block">
            {label}
          </label>
        )}
        {multiline ? (
          <textarea
            ref={ref as React.Ref<HTMLTextAreaElement>}
            rows={rows}
            className={`${inputClasses} ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''} ${className}`}
            {...(props as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
          />
        ) : (
          <input
            ref={ref as React.Ref<HTMLInputElement>}
            className={`${inputClasses} ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''} ${className}`}
            {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
          />
        )}
        {error && <p className="text-xs text-red-500">{error}</p>}
        {hint && !error && <p className="text-xs text-[var(--c-muted)]">{hint}</p>}
      </div>
    );
  }
);

FormInput.displayName = 'FormInput';

export type { FormInputProps };
